import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, normalizeApiPath, isRequestAborted } from '@core/api';

function shouldRetry(failureCount, error) {
  if (isRequestAborted(error)) return false;
  const status = error?.response?.status;
  if (status && status >= 400 && status < 500 && status !== 429) return false;
  if (!error?.response && error?.code === 'ECONNABORTED') return false;
  return failureCount < 2;
}

function normalizeHookUrl(url) {
  if (!url) {
    throw new Error('useApiQuery/useApiMutation requires a non-empty URL.');
  }
  const normalized = normalizeApiPath(url);
  if (normalized.startsWith('/v1/') || normalized.startsWith('/public/')) {
    return normalized;
  }
  throw new Error(`Unsupported API path "${normalized}". Use /v1/* or /public/*.`);
}

/* ===================== QUERY ===================== */

export function useApiQuery(key, url, options = {}) {
  return useQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: async () => {
      const { data } = await api.get(normalizeHookUrl(url));
      return data;
    },
    retry: shouldRetry,
    ...options,
  });
}

/* ===================== MUTATION ===================== */

export function useApiMutation(
  url,
  { method = 'post', invalidate = [] } = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const fn = api[method] || api.post;
      const { data } = await fn(normalizeHookUrl(url), payload);
      return data;
    },
    retry: shouldRetry,

    onSuccess: () => {
      invalidate.forEach((key) => {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(key) ? key : [key],
        });
      });
    },
  });
}